import type { AiMatch, Itinerary } from "../types";
import {
  readTrips,
  writeTrips,
  readActiveTripId,
  writeActiveTripId,
} from "../state/trip-storage";
import {
  buildTripFromItinerary,
  mergeItineraryIntoTrip,
  countStops,
} from "./itinerary-to-trip";

/**
 * Create or extend a trip in the persisted store directly (no TripProvider).
 * Used by the home concierge modal, which lives outside the discover providers.
 * Returns the resulting trip id, or null when nothing was added.
 */
export function addItineraryToStore(
  itinerary: Itinerary,
  matches: AiMatch[],
  mode: "new" | "active",
  fallbackName: string,
  /** A new trip's real first day ("2026-11-06"), when the traveller gave dates. */
  opts?: { startDate?: string },
): string | null {
  const trips = readTrips();

  if (mode === "active") {
    const activeId = readActiveTripId() ?? trips[0]?.id ?? null;
    const active = trips.find((t) => t.id === activeId) ?? null;
    if (active) {
      const merged = mergeItineraryIntoTrip(active, itinerary, matches);
      if (countStops(merged) === countStops(active)) return null;
      writeTrips(trips.map((t) => (t.id === merged.id ? merged : t)));
      writeActiveTripId(merged.id);
      return merged.id;
    }
    // No active trip — fall through to creating a new one.
  }

  const built = buildTripFromItinerary(itinerary, matches, fallbackName, opts?.startDate ? { startDate: opts.startDate } : undefined);
  if (countStops(built) === 0) return null;
  writeTrips([...trips, built]);
  writeActiveTripId(built.id);
  return built.id;
}

/** Name of the currently active trip in the store, for the chooser label. */
export function readActiveTripName(): string | null {
  const trips = readTrips();
  const activeId = readActiveTripId() ?? trips[0]?.id ?? null;
  return trips.find((t) => t.id === activeId)?.name ?? null;
}
